/*
 * File: csv.ts
 * Project: sso-frontend
 * File Created: Tuesday, 24th May 2022 5:58:01 pm
 * Author: Steven Chiffe (steven.chiffe@alyce.fr)
 * -----
 * Last Modified: Wednesday, 31st August 2022 3:56:29 pm
 * Modified By: Steven Chiffe (steven.chiffe@alyce.fr>)
 * -----
 * Copyright Py-Factory - 2022
 */

/**
 * Enum that represent OS
 *
 * @enum
 */
enum OS {
  WINDOWS = "Windows",
  LINUX = "Linux",
  X11 = "UNIX",
  MAC = "Mac"
}

class Csv {
  /**
   * Os of the user
   * @private
   * @type {string}
   * @memberof Csv
   */
  private os: OS | null;

  /**
   * Delimiter to use depending on the os
   * @private
   * @type {string}
   * @memberof Csv
   */
  private delimiter = ",";

  public constructor() {
    this.os = this.getBrowserOs();
    this.delimiter = this.determineDelimiter();
  }

  /**
   * Transform an array of object into a formatted csv string
   *
   * @param  {Array<string>} headers
   * @param  {Array<Type>} content
   * @returns string
   * @memberof Csv
   */
  public FromObjToCsv<Type>(headers: Array<string>, content: Array<Type>): string {
    let csv_string = "";
    // add headers
    csv_string += headers.join(this.delimiter) + "\n";
    // add content
    content.forEach((element) => {
      let line = "";
      headers.forEach((header) => {
        line += (element as any)[header] + this.delimiter;
      });
      csv_string += line.slice(0, -1) + "\n";
    });
    return csv_string;
  }

  /**
   * Transform any csv into an array of generics object
   *
   * @param  {string} csv_content
   * @returns Type
   * @memberof Csv
   */
  public FromCsvToArray<Type>(csv_content: string): Type[] {
    const lines = csv_content.split("\r\n");
    const headers = lines[0].split(";");
    const result: Type[] = [];
    for (let i = 1; i < lines.length - 1; i++) {
      const obj: Type = {} as unknown as Type;
      const currentline = lines[i].split(";");
      for (let j = 0; j < headers.length; j++) {
        const splitted_header_value = headers[j].split(".");
        if (splitted_header_value.length > 1) {
          if ((obj as any)[splitted_header_value[0]] === undefined) (obj as any)[splitted_header_value[0]] = {};
          (obj as any)[splitted_header_value[0]][splitted_header_value[1]] = this.determineValueType(currentline[j]);
        } else (obj as any)[headers[j]] = this.determineValueType(currentline[j]);
      }
      result.push(obj);
    }
    return result;
  }

  /**
   * Transform string value in his generics type
   *
   * @param  {string} value
   * @returns unknown
   */
  private determineValueType(value: string): any {
    if (!value) return value;
    if (value === "true") return true;
    if (value === "false") return false;
    if (value === "null") return null;
    if (value === "undefined") return undefined;
    if (value === "") return value;
    if (
      value.match(/[a-zA-Z]+/g) !== null &&
      value.match(/[a-zA-Z]+/g) !== undefined &&
      (value.match(/[a-zA-Z]+/g) as Array<string>).length > 0
    )
      return value;
    if (!isNaN(parseInt(value))) {
      if (value.split(".").length > 0 && value.split(".").length < 3) return parseFloat(value);
      else if (value.split(".").length > 2) return value;
      else return parseInt(value);
    }
    return value;
  }

  /**
   * Just a simple function to determine on what kind of plaform the browser is
   *
   * @returns OS
   * @memberof Csv
   */
  private getBrowserOs(): OS {
    // # TODO  when userAgentData api is implemented modify the condition by that navigator?.userAgentData?.platform cause navigator.plaform is deprecated ...
    let platform: OS | string = navigator?.platform || OS.WINDOWS;
    if (platform.indexOf("Mac") != -1) platform = OS.MAC;
    if (platform.indexOf("X11") != -1) platform = OS.X11;
    if (platform.indexOf("Linux") != -1) platform = OS.LINUX;
    if (platform.indexOf("Win") != -1) platform = OS.WINDOWS;
    return platform as OS;
  }

  /**
   * Determine the delimiter to use depending on the os
   *
   * @returns string
   * @memberof Csv
   */
  private determineDelimiter(): string {
    return this.os === OS.WINDOWS ? (this.delimiter = ",") : (this.delimiter = ";");
  }

  /**
   * Read the content of the csv file
   *
   * @param  {File} file
   * @returns Promise<string>
   */
  public readFileContent = async (file: File): Promise<string> => file.text();
}

export default Csv;
